import { FinanceValueObjectsFinancialDocumentOperationTypeEnum } from 'generated/resources/datatable.graphql'
import {
  FinanceValueObjectsFinancialDocumentTypeEnum,
  InvoiceFragment,
  JournalLineReconciliationFragment,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import { fromCents } from 'modules/core/lib/formatCurrencyUtils'

import { canUpdateInvoiceData } from 'modules/finance/lib/invoices'

import { InvoiceType, LegalEntity } from './types'

type FinanceInvoiceTax = NonNullable<NonNullable<InvoiceFragment['taxes']>[number]>

export interface DisabledFields {
  documentNumber?: boolean
  taxId?: boolean
  legalName?: boolean
  addressLine1?: boolean
  addressLine2?: boolean
  city?: boolean
  postalCode?: boolean
  state?: boolean
  countryCode?: boolean
  totalAmountCents?: boolean
  currency?: boolean
  documentDate?: boolean
  associatedResource?: boolean
  dueDate?: boolean
  taxes?: boolean
  legalEntity?: boolean
  ledgerAccount?: boolean
  parentFinancialDocument?: boolean
  documentType?: boolean
}

interface buildDefaultValuesProps {
  invoice: InvoiceType
  legalEntity: LegalEntity | undefined | null
  ledgerAccountId: number | undefined
}

export const buildDefaultValues = (values: buildDefaultValuesProps) => {
  const { invoice, legalEntity, ledgerAccountId } = values

  const mapTaxesToForm = (taxes: FinanceInvoiceTax[]) =>
    taxes.map(
      ({ baseAmountCents, percentage, amountCents, taxTypeId, taxTypeName, type, taxRates }) => {
        const formattedPercentage = percentage
          ? Number.parseFloat((percentage * 100).toFixed(2))
          : percentage
        return {
          id: uuid(),
          base_amount: baseAmountCents,
          percentage: formattedPercentage,
          amount: amountCents,
          tax_type_id: taxTypeId,
          tax_type_name: taxTypeName,
          type: type,
          tax_rates: taxRates?.map(({ percentage: ratePercentage }: { percentage: number }) => ({
            percentage: ratePercentage,
          })),
        }
      }
    ) ?? []

  return {
    documentNumber: invoice.documentNumber ?? '',
    dueDate: invoice.dueDate ? invoice.dueDate : undefined,
    totalAmountCents: invoice.totalAmountCents ? String(fromCents(invoice.totalAmountCents)) : '',
    currency: invoice.currency ?? undefined,
    documentDate: invoice.documentDate ?? undefined,
    taxes: mapTaxesToForm(invoice.taxes),
    status: invoice.status ?? 'processing',
    legalEntity: legalEntity,
    ledgerAccountId: ledgerAccountId,
    validatedAt: invoice.validatedAt ?? undefined,
    documentType: invoice.documentType,
    parentFinancialDocument: invoice.parentFinancialDocument,
    vendor: invoice.vendor ?? null,
  }
}

export const buildDisabledFields = (invoice: InvoiceType): DisabledFields => {
  if (canUpdateInvoiceData(invoice)) {
    return {}
  }
  return {
    documentNumber: true,
    totalAmountCents: true,
    currency: true,
    documentDate: true,
    dueDate: true,
    taxes: true,
    associatedResource: true,
    legalEntity: true,
    ledgerAccount: true,
    parentFinancialDocument: true,
    documentType: true,
  }
}

interface FinancialDocumentBase {
  documentType: FinanceValueObjectsFinancialDocumentTypeEnum
  operationType: FinanceValueObjectsFinancialDocumentOperationTypeEnum
}

export const getReconcilableJournalLine = (
  financialDocument: FinancialDocumentBase,
  journalLines: JournalLineReconciliationFragment[]
): JournalLineReconciliationFragment | undefined => {
  const documentType = financialDocument.documentType
  const operationType = financialDocument.operationType

  const isInvoiceOrReceipt = documentType === 'invoice' || documentType === 'receipt'

  const conditions: {
    [key in FinanceValueObjectsFinancialDocumentOperationTypeEnum]: keyof JournalLineReconciliationFragment
  } = {
    purchase: isInvoiceOrReceipt ? 'creditAmountCents' : 'debitAmountCents',
    sale: isInvoiceOrReceipt ? 'debitAmountCents' : 'creditAmountCents',
  }

  return journalLines.find((journalLine) => journalLine[conditions[operationType]] > 0)
}
